<template>
  <div class="otp-by-route-view">
    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>

    <div v-else>
      <!-- Legend and Sort Controls -->
      <div class="route-item mb-5">
        <div class="fw-bold">
          {{ $t('flexigo.otp.allRoutes') }}
        </div>
        <div class="d-flex justify-content-between align-items-center flex-wrap flex-row">
          <otp-legend :items="legendItems" :selected="selectedStatuses" @status-click="handleStatusFilter" />
          <div class="d-flex align-items-center gap-3">
            <otp-sort-control v-model="sortBy" />
            <export-graph
              :routes="visibleRoutes"
              :date-range="filters.dateRange"
              :threshold="threshold"
              filename="otp-by-route"
            />
          </div>
        </div>
      </div>

      <!-- Routes List -->
      <div class="routes-list">
        <div v-for="route in visibleRoutes" :key="route.id" class="route-item">
          <div class="route-name">
            <a href="#" class="route-link" @click.prevent="handleRouteClick(route.id)">
              {{ route.name }}
            </a>
          </div>
          <stacked-bar-chart
            :data="route.stats"
            :route-id="route.id"
            :route-name="route.name"
            :date-range="filters.dateRange"
            @bar-click="handleRouteClick(route.id)"
          />
        </div>
      </div>

      <!-- Show More / Show Less -->
      <div v-if="totalRoutes > 10" class="text-center mt-3">
        <a v-if="showLess" href="#" class="me-3" @click.prevent="handleShowLess">
          {{ $t('flexigo.otp.showLess') }}
        </a>
        <a v-if="hasMore" href="#" @click.prevent="handleShowMore">
          {{ $t('flexigo.otp.showMore') }}
        </a>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import OtpLegend from '../charts/OtpLegend.vue';
import StackedBarChart from '../charts/StackedBarChart.vue';
import ExportGraph from '../components/ExportGraph.vue';
import OtpSortControl from '../components/OtpSortControl.vue';

export default {
  name: 'OtpByRoute',
  components: {
    OtpLegend,
    StackedBarChart,
    ExportGraph,
    OtpSortControl,
  },
  props: {
    filters: {
      type: Object,
      required: true,
    },
    threshold: {
      type: Number,
      default: 5,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      sortBy: 'onTimeAsc',
      selectedStatuses: ['late', 'onTime', 'early', 'skipped'],
      displayCount: 10,
      legendItems: [
        { label: 'Late', color: '#ff4059', key: 'late' },
        { label: 'On Time', color: '#687692', key: 'onTime' },
        { label: 'Early Departure', color: '#5287e3', key: 'early' },
        { label: 'Skipped', color: '#97a0b4', key: 'skipped' },
      ],
    };
  },
  computed: {
    ...mapGetters('otpDashboard', ['routeStats']),

    processedRoutes() {
      // Use routeStats from Vuex store
      return this.routeStats.map((routeStat) => ({
        id: routeStat.routeId,
        name: routeStat.routeName,
        stats: routeStat.stats,
      }));
    },
    sortedRoutes() {
      const routes = [...this.processedRoutes];
      const { sortBy } = this;

      routes.sort((a, b) => {
        if (sortBy === 'onTimeAsc') return a.stats.onTime - b.stats.onTime;
        if (sortBy === 'onTimeDesc') return b.stats.onTime - a.stats.onTime;
        if (sortBy === 'lateAsc') return a.stats.late - b.stats.late;
        if (sortBy === 'lateDesc') return b.stats.late - a.stats.late;
        if (sortBy === 'earlyAsc') return a.stats.early - b.stats.early;
        if (sortBy === 'earlyDesc') return b.stats.early - a.stats.early;
        return 0;
      });

      return routes;
    },
    filteredRoutes() {
      if (this.selectedStatuses.length === 0) {
        return this.sortedRoutes;
      }
      return this.sortedRoutes.filter((route) => this.selectedStatuses.some((status) => route.stats[status] > 0));
    },
    visibleRoutes() {
      return this.filteredRoutes.slice(0, this.displayCount);
    },
    totalRoutes() {
      return this.filteredRoutes.length;
    },
    hasMore() {
      return this.displayCount < this.totalRoutes;
    },
    showLess() {
      return this.displayCount > 10;
    },
  },
  methods: {
    handleStatusFilter(status) {
      const index = this.selectedStatuses.indexOf(status);
      if (index > -1) {
        this.selectedStatuses.splice(index, 1);
      } else {
        this.selectedStatuses.push(status);
      }
    },
    handleRouteClick(routeId) {
      this.$emit('route-selected', routeId);
    },
    handleShowMore() {
      this.displayCount += 10;
    },
    handleShowLess() {
      this.displayCount = Math.max(10, this.displayCount - 10);
    },
  },
};
</script>

<style scoped>
.route-item {
  display: grid;
  grid-template-columns: 2fr 5fr;
  gap: 1rem;
  align-items: center;
  margin-bottom: 1em;
}
.otp-by-route-view {
  padding: 20px;
  position: relative;
}

.route-item {
  background: #fff;
}

.route-name {
  font-size: 0.9rem;
  font-weight: 500;
  text-align: right;
}

.route-link {
  color: #000b20;
  text-decoration: none;
}

.route-link:hover {
  color: #5667ff;
}
</style>
